import { SharedValue, WithSpringConfig } from 'react-native-reanimated';

export const PAGE_BUFFER = 2; // 2 pages on each side
export const CIRCULAR_LENGTH = PAGE_BUFFER * 2 + 1; // "virtual" length for circular wrapping - 5 pages in total
export const HALF_LENGTH = CIRCULAR_LENGTH / 2; // e.g., 2.5 if length=5

// the offsets for the pages in the circular pager
// [-2, -1, 0, 1, 2] for a length of 5
export const STABLE_OFFSETS = Array.from({ length: PAGE_BUFFER * 2 + 1 }, (_, i) => i - PAGE_BUFFER);

export const wrapToHalfLength = (rawDiff: number) => {
    'worklet';
    /**
     * suppose we have a list of 5 items (length = 5), indexed as:
     * [0, 1, 2, 3, 4]
     *
     * if `pageAnim.value = 4.8` and we check `index = 0`:
     * diff = 0 - 4.8 = -4.8 (this is out of range)
     *
     * The goal is to wrap this value so that instead of -4.8, we get 0.2
     */

    // we normalize the difference to ensure it stays within the valid circular range.
    // the modular arithmetic below ensures that values wrap around properly.
    let wrapped = (((rawDiff + HALF_LENGTH) % CIRCULAR_LENGTH) + CIRCULAR_LENGTH) % CIRCULAR_LENGTH;

    /**
     * Example with values to illustrate the wrapping:
     * Given:
     * - `length = 5`
     * - `halfLength = length / 2 = 5 / 2 = 2.5`
     * - `pageAnim.value = 4.8`
     * - `index = 0`
     *
     * step-by-step calculations:
     *
     * 1. Compute the initial difference - this is the parameter received by this function
     *    diff = index - pageAnim.value
     *         = 0 - 4.8
     *         = -4.8
     *
     * 2. Add halfLength to shift the range:
     *    diff + halfLength = -4.8 + 2.5
     *                      = -2.3
     *
     * 3. First modulo operation to wrap inside [0, length]:
     *    (-2.3 % 5) → In JavaScript, negative modulo results can be tricky.
     *    -2.3 % 5 = -2.3 (but in some languages, it would give 2.7)
     *
     *    To ensure positivity, we do:
     *    (-2.3 + 5) % 5 = 2.7
     *
     * 4. Apply the second modulo to keep it within [0, length]:
     *    (2.7 % 5) = 2.7 (unchanged, still within range)
     *
     * 5. Shift back by subtracting `halfLength`:
     *    2.7 - 2.5 = 0.2
     *
     * Final result: `diff = 0.2`
     *
     * This means index `0` is **actually close to** index `4.8` in a circular fashion.
     */

    // Step 3: Return the wrapped difference, which represents how far the item is from focus.
    wrapped = wrapped - HALF_LENGTH;

    return wrapped;
};

/**
 * Check if the current sheet is active based on the focus animation value and the next active target.
 * Set as active if the animation is almost completed or the next active page is the current sheet.
 *
 * @param focusAnim - The animated value representing the focus position.
 * @param nextActivePage - The target index for the next active sheet.
 * @param offset - The offset of the current sheet.
 */
export const checkIfSheetIsActive = ({
    focusAnim,
    nextActivePage,
    offset,
}: {
    focusAnim: SharedValue<number>;
    nextActivePage: SharedValue<number | null>;
    offset: number;
}) => {
    'worklet';

    const isOffsetTheNextTargetPage =
        nextActivePage.value !== null && wrapToHalfLength(nextActivePage.value) === offset;

    return isOffsetTheNextTargetPage || Math.abs(focusAnim.value) < 0.5;
};

export const DEFAULT_ANIMATION_CONFIG: WithSpringConfig = {
    damping: 20,
    mass: 0.2,
    stiffness: 100,
    overshootClamping: false,
    restSpeedThreshold: 0.2,
    restDisplacementThreshold: 0.2,
};

export const FLING_VELOCITY = 500;

// safe modulo function that handles negative values properly.
export const moddedIndex = (index: number, length: number) => {
    'worklet';
    return ((index % length) + length) % length;
};

/**
 * Count how many times an element (represented by internal index i)
 * has "toggled" (or shifted) given the current index n.
 *
 * The internal index i (from 0 to 4) maps to display offsets as follows:
 *   i = 0  --> offset -2
 *   i = 1  --> offset -1
 *   i = 2  --> offset  0
 *   i = 3  --> offset  1
 *   i = 4  --> offset  2
 *
 * There are two modes:
 * 1. Forward toggles (n >= 0):
 *    - For example, if i = 0 (offset -2), toggles occur at n = 1, 6, 11, 16, ...
 *    - Similarly, if i = 4 (offset 2), toggles occur at n = 5, 10, 15, 20, ...
 *
 * 2. Backward toggles (n < 0):
 *    - For i = 4 (offset 2), toggles occur at n = -1, -6, -11, -16, ...
 *    - And for i = 0 (offset -2), toggles occur at n = -5, -10, -15, -20, ...
 *
 * Depending on whether n is positive or negative, we route to the proper helper.
 */
export const countToggles = (i: number, n: number): number => {
    // if n is 0, no toggles have occurred.
    if (n === 0) {
        return 0;
    }
    // for positive indices, use forward toggling logic.
    else if (n > 0) {
        return togglesForward(i, n);
    }
    // for negative indices, use backward toggling logic.
    else {
        return togglesBackward(i, n);
    }
};

/**
 * Compute the number of forward toggles for a given offset index i and positive n.
 *
 * For forward toggling:
 * - The first toggle happens when n equals (i + 1).
 * - Then, every additional 5 steps (i.e. n increases by 5) results in another toggle.
 *
 * Example: For i = 4 (which corresponds to offset 2):
 *   - The first toggle is at n = 5.
 *   - Then toggles occur at n = 10, 15, 20, etc.
 */
export const togglesForward = (i: number, n: number): number => {
    // The first toggle for offset i happens at n = (i + 1).
    const first = i + 1; // e.g., for i=4, first toggle at n = 5.

    // If n is less than the first toggle moment, then no toggles have occurred.
    if (n < first) {
        return 0;
    }
    // Count the initial toggle plus every full step of 5 beyond the first.
    return 1 + Math.floor((n - first) / CIRCULAR_LENGTH);
};

/**
 * Compute the number of backward toggles for a given offset index i and negative n.
 *
 * For backward toggling:
 * - The first toggle happens at n = -(5 - i).
 * - Then, toggles occur every additional -5 steps (i.e. n decreases by 5).
 *
 * Example: For i = 4 (corresponding to offset 2):
 *   - The first toggle is at n = -1 (since -(5 - 4) = -1).
 *   - Then toggles occur at n = -6, -11, -16, etc.
 *
 */
export const togglesBackward = (i: number, n: number): number => {
    // calculate the first toggle point: n = -(5 - i)
    const first = -(CIRCULAR_LENGTH - i); // e.g., for i=4, first = -1.

    // if n is greater than the first toggle (i.e. less negative), then no toggles have occurred.
    if (n > first) {
        return 0;
    }
    // calculate the number of full -5 steps that fit between 'first' and n.
    // we need m such that: first - 5*m >= n.
    const m = Math.floor((first - n) / CIRCULAR_LENGTH);

    // add 1 to account for the first toggle, ensuring m is non-negative.
    return m >= 0 ? m + 1 : 0;
};

/**
 * Determine the step size added with each toggle, based on the current index.
 *
 * The step depends on whether we are moving forward or backward:
 * - For forward toggles (currentIndex >= 0): the step is +5.
 * - For backward toggles (currentIndex < 0): the step is (length - 5),
 *   which works out as subtracting 5 when using modulo arithmetic.
 */
export const getStep = (currentIndex: number, length: number): number => {
    // return 5 for forward toggling; for backward toggling, use (length - 5)
    return currentIndex >= 0 ? CIRCULAR_LENGTH : length - CIRCULAR_LENGTH;
};

/**
 * Computes the actual index of a page using a circular, round-robin translation strategy.
 *
 * Internally, a fixed offset array is maintained: [-2, -1, 0, 1, 2]
 * This offset array never changes; only the translation of pages is adjusted upon swipe events.
 *
 * Detailed Mapping Examples:
 * - Initial render (currentIndex = 0):
 *   Offsets:    [-2, -1, 0, 1, 2]
 *   Actual Indices computed by this function:    [length-2, length-1, 0, 1, 2]
 *
 * - After one swipe forward (currentIndex = 1):
 *   Offsets remain: [-2, -1, 0, 1, 2]
 *   Visual representation: [-1, 0, 1, 2, -2]
 *   Actual Indices computed by this function: [3, length - 1, 0, 1, 2]
 *
 * - After two swipes forward (currentIndex = 2):
 *   Offsets remain: [-2, -1, 0, 1, 2]
 *   Visual representation: [0, 1, 2, -2, -1]
 *   Actual Indices computed by this function:: [3, 4, 0, 1, 2]
 *
 * ! The visual representation shifts with each swipe, but the underlying offset array is constant.
 * ! This means that on a swipe, only one page actually changes its index.
 * ! When we swipe forward in the initial render, the leftmost page (offset -2) becomes the rightmost page (offset 2).
 * ! This means that only the leftmost page is actually changing its index. going from length-2 to 3.
 * ! The same applies for backward swipes, but reversed,
 * ! where the rightmost page (offset 2) becomes the leftmost page (offset -2).
 *
 * If we analyze a concrete examples we can see a pattern
 * Let's assume a length of 10, starting index = 0, and only swiping forward
 * Each row bellow represents a swipe forward, and represent the data that needs to be returned for [-2, -1, 0, 1, 2]
 * 8,9,0,1,2
 * 3,9,0,1,2
 * 3,4,0,1,2
 * 3,4,5,1,2
 * 3,4,5,6,2
 * 3,4,5,6,7
 * 8,4,5,6,7
 * 8,9,5,6,7
 * 8,9,0,6,7
 * 8,9,0,1,7
 * 8,9,0,1,2
 *
 * We can observe the elements toggle back and forth between some values every 5 swipes
 * We will call this "toggling", which occurs every 5 steps, starting on a different index depending on the offset
 * To find the actual index, we need to know how many times the offset has toggled
 *
 *
 * Calculation Steps:
 * 1. Convert offset to internal index: (i = offset + 2).
 * 2. Identify base index from the initial mapping [length-2, length-1, 0, 1, 2].
 * 3. Compute how many toggles have occurred (t) using countToggles().
 * 4. Calculate the step using getStep() (returns either +5 or length-5).
 * 5. Derive final index with:
 *      finalIndex = (base[i] + step * t) mod length
 *
 * @param offset - Relative page offset (-2, -1, 0, 1, or 2)
 * @param currentIndex - Current active page index (can be negative for backwards toggling)
 * @param length - Total number of items in our list
 * @returns Computed actual index within bounds [0, length - 1]
 */
export const getRealIndex = (offset: number, currentIndex: number, length: number): number => {
    // map the offset (-2 to 2) to an internal index (0 to 4).
    // this is done just for easier calculations.
    const i = offset + 2;

    // establish the base indices corresponding to each offset when currentIndex = 0.
    // the modulo operation ensures we wrap around correctly if length is small.
    const base = [(length - 2) % length, (length - 1) % length, 0, 1, 2];

    // determine how many toggles have occurred for this internal index i.
    const t = countToggles(i, currentIndex);

    // choose the appropriate step value based on the direction (forward or backward).
    const step = getStep(currentIndex, length);

    // calculate the final index by shifting the base index by (step * toggles)
    // and then taking the modulo to ensure the result is within array bounds.
    const result = (base[i] + step * t) % length;

    return (result + length) % length;
};

/**
 * Moves currentIndex to targetIndex via the shortest route on a circular list.
 *
 * Example:
 * - If length=8, currentIndex=9 (which effectively means page 1),
 *   and you want to go to targetIndex=2,
 *   this should jump forward by 1 to get 10 (which is effectively page 2).
 *
 * @param currentIndex - Your current position (could be any integer, positive or negative)
 * @param targetIndex  - The desired position (could be any integer, positive or negative)
 * @param length       - The total number of pages (or items) in the circular list
 * @returns A new "currentIndex" after making the minimal circular jump
 */
export const moveToClosestIndex = (currentIndex: number, targetIndex: number, length: number): number => {
    'worklet';

    // 1. "Normalize" both indices so they're within 0..length-1
    //    Even if they're negative or bigger than length,
    //    moddedIndex brings them back into the valid circular range.
    const ciNorm = moddedIndex(currentIndex, length);
    const tiNorm = moddedIndex(targetIndex, length);

    // 2. Figure out how far we need to move in the "forward" direction.
    //    This difference is also normalized so it's within 0..length-1.
    let dRaw = moddedIndex(tiNorm - ciNorm, length);

    // 3. If that forward difference is more than half the circle,
    //    it's shorter to wrap around the other way (i.e., move backward).
    if (dRaw > length / 2) {
        dRaw -= length;
    }

    // 4. Now apply that offset to the original currentIndex.
    //    Note that currentIndex itself might be outside 0..length-1,
    //    so we just shift it by the minimal offset.
    return currentIndex + dRaw;
};
